// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import pluralize from 'pluralize';

import Button from 'components/Button';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import Menu from 'components/Menu';
import { selectRecipient, RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import TargetFilterMenuItem from 'chat/components/TargetFilterMenuItem';

import type { MessageFilterType, Recipient } from 'reducers/roleBasedMessagingSearch';

import AppleImage from 'assets/images/roles/teacher.svg';
import BellImage from 'assets/images/bell.svg';
import HeartImage from 'assets/images/roles/parent.svg';
import PencilImage from 'assets/images/roles/student.svg';
import EveryoneImage from 'assets/images/roles/everyone.svg';

type Props = {
  filter: *,
  recipient: Recipient,
  dataTestSelector: ?string,
  recipientIsSelected: boolean,

  // Redux state
  selection: Recipient[],
  districtIsSelected: boolean,

  // Redux dispatchers
  onSelectRecipient: (recipient: Recipient, filter: ?MessageFilterType) => *,
};

type State = {
  menuIsOpen: boolean,
};

class TargetFilterMenu extends React.Component<Props, State> {
  state = {
    menuIsOpen: false,
  };

  filterIsSelected = (filter: *) => {
    return this.getSelectedFilters(filter.type).some(
      selectedFilter => selectedFilter.value.id === filter.value.id
    );
  };

  getSelectedFilters = (type: string): *[] => {
    const recipientInSelection = this.props.selection.find(
      selectedRecipient => selectedRecipient.id === this.props.recipient.id
    );

    if (
      recipientInSelection &&
      recipientInSelection.type !== RECIPIENT_TYPES.ORGANIZATION_RECIPIENT &&
      recipientInSelection.type !== RECIPIENT_TYPES.GROUP_RECIPIENT
    ) {
      return [];
    }

    return recipientInSelection && recipientInSelection.selectedFilters
      ? recipientInSelection.selectedFilters.filter(selectedFilter => selectedFilter.type === type)
      : [];
  };

  deselectFullRecipient = () => {
    EventTracking.trackKey('announcements.share_to.role.unselect.click', { context: 'everyone' });
    this.props.onSelectRecipient(this.props.recipient, null);
  };

  handleMenuItemSelection = (event: SyntheticMouseEvent<>, filter: MessageFilterType) => {
    event.stopPropagation();

    const roleContext = filter.value.id === 'all' ? 'everyone' : filter.value.id;
    if (this.filterIsSelected(filter)) {
      EventTracking.trackKey('announcements.share_to.role.unselect.click', {
        context: roleContext,
      });
    } else {
      EventTracking.trackKey('announcements.share_to.role.select.click', {
        context: roleContext,
      });
    }

    if (filter.value.id === 'all' && this.recipientIsSelectedAndUnfiltered(filter.type)) {
      // TODO: once we have more than one filter type, we should remove this and
      // re-evaluate the behavior that happens when the user toggles the "all"
      // value of any particular filter type
      this.deselectFullRecipient();
      return;
    }
    this.props.onSelectRecipient(this.props.recipient, filter);
  };

  makeMessageFilter = (type: string, value: *) => {
    return { type, value };
  };

  recipientIsSelectedAndUnfiltered = (filterType: string) => {
    return this.props.recipientIsSelected && !this.getSelectedFilters(filterType).length;
  };

  renderFilterMenu = (filterType: string, values: *[]) => {
    const { recipient } = this.props;
    const roleToIcon = {
      student: PencilImage,
      parent: HeartImage,
      teacher: AppleImage,
      admin: BellImage,
    };
    const orgUuid =
      recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT
        ? recipient.organization.uuid
        : undefined;
    const groupUuid =
      recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT ? recipient.group.uuid : undefined;
    const allFilter = this.makeMessageFilter(filterType, {
      id: 'all',
      label: `All ${filterType}`,
    });

    return (
      <Menu className="RecipientItem-menu" hideOnSelect={false}>
        <TargetFilterMenuItem
          filter={allFilter}
          imageSrc={EveryoneImage}
          isSelected={this.recipientIsSelectedAndUnfiltered(filterType)}
          orgUuid={orgUuid}
          groupUuid={groupUuid}
          onClick={e => this.handleMenuItemSelection(e, allFilter)}
        />
        {values.map(value => {
          const image = roleToIcon[value.id];
          const messageFilter = this.makeMessageFilter(filterType, value);
          return (
            <TargetFilterMenuItem
              key={value.id}
              filter={messageFilter}
              imageSrc={image}
              isSelected={this.filterIsSelected(messageFilter)}
              orgUuid={orgUuid}
              groupUuid={groupUuid}
              dataTestSelector={this.props.dataTestSelector}
              onClick={e => this.handleMenuItemSelection(e, messageFilter)}
            />
          );
        })}
      </Menu>
    );
  };

  toggleMenu = (event: SyntheticMouseEvent<>) => {
    event.stopPropagation();
    this.refs.trigger.toggleLayer();
  };

  render() {
    const { filter } = this.props;
    const numberOfSelectedFilters = this.getSelectedFilters(filter.type).length;
    const selectedFilterText =
      numberOfSelectedFilters < 3
        ? this.getSelectedFilters(filter.type)
            .map(selectedFilter => selectedFilter.value.label)
            .join(', ')
        : `${numberOfSelectedFilters} ${pluralize(filter.type, 2).toLowerCase()}`;
    return (
      <LayerTrigger
        ref="trigger"
        trigger="manual"
        hideLayerOnScroll
        layer={this.renderFilterMenu(filter.type, filter.values)}
        placement="bottom"
        alignment="left"
        onHide={() => {
          this.setState({ menuIsOpen: false });
          EventTracking.trackKey('announcements.share_to.refine_recipients.role.close');
        }}
        onShow={() => {
          this.setState({ menuIsOpen: true });
          EventTracking.trackKey('announcements.share_to.refine_recipients.role.open');
        }}>
        <Button
          onClick={this.toggleMenu}
          size="xxxsmall"
          color={this.state.menuIsOpen ? 'brand' : 'polar-bear'}
          weight="normal"
          data-test-selector={this.props.dataTestSelector}
          className="RecipientItem-button">
          <MediaObject inline figure="right" gutter="xsmall">
            {this.recipientIsSelectedAndUnfiltered(filter.type) || !this.props.recipientIsSelected
              ? 'Everyone'
              : selectedFilterText}
            <Glyph name="chevron-down" size="xxsmall" solo />
          </MediaObject>
        </Button>
      </LayerTrigger>
    );
  }
}

const mapStateToProps = (state: Object) => {
  return {
    districtIsSelected: state.roleBasedMessagingSearch.districtIsSelected,
    selection: state.roleBasedMessagingSearch.selection,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
    },
    dispatch
  );
};

export { TargetFilterMenu };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TargetFilterMenu);
